package com.rosario.juridico.marketplace.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.PerfilAbogadoDataModel
import com.rosario.juridico.domain.ConsultaDataModel
import com.rosario.juridico.marketplace.model.Consulta
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState

import com.rosario.juridico.marketplace.viewmodel.DirectorioViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PanelAdminScreen(navController: NavController, viewModel: MarketplaceViewModel, directorioViewModel: DirectorioViewModel) {
    val consultasPendientesState by viewModel.consultasPendientesState.collectAsState()
    val abogadosActivosState by directorioViewModel.directorioAbogadosState.collectAsState()
    val perfilesPendientesState by viewModel.perfilesPendientesState.collectAsState()

    var tabIndex by remember { mutableStateOf(0) }
    val tabs = listOf("Pendientes", "Abogados Activos", "Matrículas Pendientes")

    LaunchedEffect(Unit) {
        viewModel.loadConsultasPendientes()
        directorioViewModel.loadDirectorioAbogados()
        viewModel.loadPerfilesPendientes()
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Panel de Administración") },
                navigationIcon = {
                    IconButton(onClick = { navController.popBackStack() }) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Atrás")
                    }
                }
            )
        }
    ) { padding ->
        Column(modifier = Modifier.padding(padding)) {
            ScrollableTabRow(selectedTabIndex = tabIndex) {
                tabs.forEachIndexed { index, title ->
                    Tab(
                        selected = tabIndex == index,
                        onClick = { tabIndex = index },
                        text = { Text(title) }
                    )
                }
            }

            when (tabIndex) {
                0 -> {
                    ListaConsultasAdmin(
                        state = consultasPendientesState,
                        onAprobar = { viewModel.aprobarConsulta(it) },
                        onRechazar = { id, motivo -> viewModel.rechazarConsulta(id, motivo) },
                        onEliminar = { viewModel.eliminarConsulta(it) },
                        esPendiente = true
                    )
                }
                1 -> {
                    ListaAbogadosActivosAdmin(
                        state = abogadosActivosState
                    )
                }
                2 -> {
                    ListaAbogadosPendientesAdmin(
                        state = perfilesPendientesState,
                        onAprobarAbogado = { viewModel.aprobarPerfilAbogado(it) }
                    )
                }
            }
        }
    }
}

@Composable
fun ListaAbogadosPendientesAdmin(
    state: UiState<List<PerfilAbogadoDataModel>>,
    onAprobarAbogado: (String) -> Unit
) {
    when (state) {
        is UiState.Loading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
        is UiState.Error -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { Text("Error: ${state.message}") }
        is UiState.Success -> {
            val abogados = state.data
            if (abogados.isEmpty()) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text("No hay solicitudes de matrícula pendientes")
                }
            } else {
                LazyColumn(
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(abogados) { abogado ->
                        AbogadoPendienteAdminCard(
                            abogado = abogado,
                            onAprobarAbogado = onAprobarAbogado
                        )
                    }
                }
            }
        }
        else -> {}
    }
}

@Composable
fun AbogadoPendienteAdminCard(
    abogado: PerfilAbogadoDataModel,
    onAprobarAbogado: (String) -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    color = Color(0xFFFFF3E0),
                    shape = RoundedCornerShape(4.dp)
                ) {
                    Text(
                        text = "NIVEL 2 - EN REVISIÓN",
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = Color(0xFFE65100),
                        fontWeight = FontWeight.Bold
                    )
                }
                Spacer(Modifier.weight(1f))
                Icon(Icons.Default.VerifiedUser, null, tint = Color(0xFFE65100), modifier = Modifier.size(18.dp))
            }

            Text(
                text = abogado.nombreCompleto.ifBlank { "Dr./Dra. Profesional" },
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )

            Text(
                text = "Matrícula: ${abogado.matricula?.ifBlank { "En trámite" } ?: "Sin especificar"}",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.primary
            )

            Text(
                text = "Teléfono: ${abogado.telefono?.ifBlank { "No informado" } ?: "No informado"}",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            Text(
                text = "UID: ${abogado.abogadoUid}",
                style = MaterialTheme.typography.labelSmall,
                color = Color.Gray
            )

            Spacer(Modifier.height(8.dp))

            Button(
                onClick = { onAprobarAbogado(abogado.abogadoUid) },
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2E7D32)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Icon(Icons.Default.CheckCircle, null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text("Aprobar Matrícula (Avanzar a Nivel 3)", fontWeight = FontWeight.Bold)
            }
        }
    }
}

@Composable
fun ListaConsultasAdmin(
    state: UiState<List<Consulta>>,
    onAprobar: (String) -> Unit,
    onRechazar: (String, String) -> Unit,
    onEliminar: (String) -> Unit,
    esPendiente: Boolean
) {
    when (state) {
        is UiState.Loading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
        is UiState.Error -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { Text("Error: ${state.message}") }
        is UiState.Success -> {
            val consultas = state.data
            if (consultas.isEmpty()) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text("No hay consultas en esta sección")
                }
            } else {
                LazyColumn(
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(consultas) { consulta ->
                        ConsultaAdminCard(
                            consulta = consulta,
                            onAprobar = onAprobar,
                            onRechazar = onRechazar,
                            onEliminar = onEliminar,
                            esPendiente = esPendiente
                        )
                    }
                }
            }
        }
        else -> {}
    }
}

@Composable
fun ConsultaAdminCard(
    consulta: Consulta,
    onAprobar: (String) -> Unit,
    onRechazar: (String, String) -> Unit,
    onEliminar: (String) -> Unit,
    esPendiente: Boolean
) {
    var showRechazoDialog by remember { mutableStateOf(false) }
    var motivoRechazo by remember { mutableStateOf("") }

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    color = MaterialTheme.colorScheme.primaryContainer,
                    shape = RoundedCornerShape(4.dp)
                ) {
                    Text(
                        text = (consulta.categoria ?: "").ifBlank { "Consulta Legal" },
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                }
                Spacer(Modifier.weight(1f))
                Text(
                    text = consulta.estado.uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = when(consulta.estado) {
                        "pendiente" -> Color(0xFFF57C00)
                        "aprobada" -> Color(0xFF2E7D32)
                        else -> Color.Gray
                    }
                )
            }
            Spacer(Modifier.height(8.dp))
            Text(text = consulta.descripcion, style = MaterialTheme.typography.bodyMedium)
            Spacer(Modifier.height(8.dp))
            Text(text = "Usuario ID: ${consulta.userId}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            Text(text = "Ubicación: ${consulta.ubicacion}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            if (consulta.telefono.isNotBlank()) {
                Text(text = "Tel: ${consulta.telefono}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            }
            
            Spacer(Modifier.height(16.dp))
            
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                if (esPendiente) {
                    TextButton(onClick = { showRechazoDialog = true }, colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)) {
                        Icon(Icons.Default.Close, null)
                        Spacer(Modifier.width(4.dp))
                        Text("Rechazar")
                    }
                    Button(onClick = { onAprobar(consulta.id) }, colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2E7D32))) {
                        Icon(Icons.Default.Check, null)
                        Spacer(Modifier.width(4.dp))
                        Text("Aprobar")
                    }
                } else {
                    IconButton(onClick = { onEliminar(consulta.id) }) {
                        Icon(Icons.Default.Delete, null, tint = MaterialTheme.colorScheme.error)
                    }
                }
            }
        }
    }

    if (showRechazoDialog) {
        AlertDialog(
            onDismissRequest = { showRechazoDialog = false },
            title = { Text("Rechazar Consulta") },
            text = {
                Column {
                    Text("Ingresa el motivo del rechazo:")
                    Spacer(Modifier.height(8.dp))
                    OutlinedTextField(
                        value = motivoRechazo,
                        onValueChange = { motivoRechazo = it },
                        placeholder = { Text("Ej: No cumple con las políticas") },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        onRechazar(consulta.id, motivoRechazo)
                        showRechazoDialog = false
                    },
                    enabled = motivoRechazo.isNotBlank()
                ) {
                    Text("Confirmar Rechazo")
                }
            },
            dismissButton = {
                TextButton(onClick = { showRechazoDialog = false }) {
                    Text("Cancelar")
                }
            }
        )
    }
}

@Composable
fun ListaConsultasDataAdmin(
    state: UiState<List<ConsultaDataModel>>,
    onEliminar: (String) -> Unit
) {
    when (state) {
        is UiState.Loading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
        is UiState.Error -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { Text("Error: ${state.message}") }
        is UiState.Success -> {
            val consultas = state.data
            if (consultas.isEmpty()) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text("No hay consultas en esta sección")
                }
            } else {
                LazyColumn(
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(consultas) { consulta ->
                        ConsultaDataAdminCard(
                            consulta = consulta,
                            onEliminar = onEliminar
                        )
                    }
                }
            }
        }
        else -> {}
    }
}

@Composable
fun ConsultaDataAdminCard(
    consulta: ConsultaDataModel,
    onEliminar: (String) -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    color = MaterialTheme.colorScheme.primaryContainer,
                    shape = RoundedCornerShape(4.dp)
                ) {
                    Text(
                        text = (consulta.categoria ?: "").ifBlank { "Consulta Legal" },
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                }
                Spacer(Modifier.weight(1f))
                Text(
                    text = (consulta.estado ?: "pendiente").uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF2E7D32)
                )
            }
            Spacer(Modifier.height(8.dp))
            Text(text = consulta.descripcion ?: "", style = MaterialTheme.typography.bodyMedium)
            Spacer(Modifier.height(8.dp))
            Text(text = "Usuario ID: ${consulta.userId ?: ""}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            Text(text = "Ubicación: ${consulta.ubicacion ?: ""}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            if (!consulta.telefono.isNullOrBlank()) {
                Text(text = "Tel: ${consulta.telefono}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            }
            
            Spacer(Modifier.height(16.dp))
            
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                IconButton(onClick = { onEliminar(consulta.id ?: "") }) {
                    Icon(Icons.Default.Delete, null, tint = MaterialTheme.colorScheme.error)
                }
            }
        }
    }
}

@Composable
fun ListaAbogadosActivosAdmin(
    state: UiState<List<PerfilAbogadoDataModel>>
) {
    when (state) {
        is UiState.Loading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
        is UiState.Error -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { Text("Error: ${state.message}") }
        is UiState.Success -> {
            val abogados = state.data
            if (abogados.isEmpty()) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text("No hay abogados verificados activos")
                }
            } else {
                LazyColumn(
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(abogados) { abogado ->
                        AbogadoActivoAdminCard(
                            abogado = abogado
                        )
                    }
                }
            }
        }
        else -> {}
    }
}

@Composable
fun AbogadoActivoAdminCard(
    abogado: PerfilAbogadoDataModel
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    color = Color(0xFFE8F5E9),
                    shape = RoundedCornerShape(4.dp)
                ) {
                    Text(
                        text = "NIVEL 3 - VERIFICADO",
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = Color(0xFF2E7D32),
                        fontWeight = FontWeight.Bold
                    )
                }
                Spacer(Modifier.weight(1f))
                Icon(Icons.Default.Verified, null, tint = Color(0xFF2E7D32), modifier = Modifier.size(18.dp))
            }

            Text(
                text = abogado.nombreCompleto.ifBlank { "Dr./Dra. Profesional" },
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )

            Text(
                text = "Matrícula: ${abogado.matricula?.ifBlank { "Registrada" } ?: "Registrada"}",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.primary
            )

            if (abogado.especialidades.isNotEmpty()) {
                Text(
                    text = "Especialidades: ${abogado.especialidades.joinToString(", ")}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            Text(
                text = "Jurisdicción: ${abogado.jurisdiccion.ifBlank { "Rosario" }}",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            if (!abogado.telefono.isNullOrBlank()) {
                Text(
                    text = "Teléfono: ${abogado.telefono}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            Text(
                text = "UID: ${abogado.abogadoUid}",
                style = MaterialTheme.typography.labelSmall,
                color = Color.Gray
            )
        }
    }
}
